import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const ReadingPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const exampleQuestion = exerciseData.questions.find(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index !== 0).sort((a, b) => a.order_index - b.order_index);

  const handleAnswerChange = (questionId, event) => {
    setAnswers(prev => ({ ...prev, [questionId]: event.target.value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-4">
        {/* Example question */}
        {exampleQuestion && (
          <div className="bg-white dark:bg-slate-800/40 rounded-3xl border-2 border-amber-200/50 p-3 md:p-8 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16 blur-2xl" />
            <div className="flex flex-col md:flex-row md:items-center gap-4 relative z-10">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="shrink-0 font-black text-white bg-amber-500 px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider shadow-sm">e.g.</span>
                <p className="font-bold text-slate-700 dark:text-slate-300 text-base md:text-lg leading-snug m-0 flex-1">{exampleQuestion.question_text.replace(/\(Example\)/gi, '').trim()}</p>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto">
                <input
                  type="text"
                  value={exampleQuestion.accepted_answers[0]}
                  disabled
                  className="exercise-input-field exercise-input-text exercise-example-text w-full md:w-44 text-center py-3 bg-slate-50 dark:bg-slate-900/50 border-transparent font-black italic opacity-60"
                />
              </div>
            </div>
          </div>
        )}

        {/* Main questions */}
        <div className="space-y-4">
          {mainQuestions.map((q) => {
            const isAnswerCorrect = q.is_correct === true || q.is_correct === 'true';
            const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

            return (
              <div key={q.id} className="bg-white dark:bg-slate-800/40 rounded-3xl border border-slate-100 dark:border-slate-700 p-3 md:p-8 shadow-sm hover:border-slate-200 transition-all flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <span className="font-black text-slate-400 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl text-sm border border-slate-100 dark:border-slate-800 shadow-sm shrink-0">
                    {q.order_index}.
                  </span>
                  <p className="font-bold text-slate-800 dark:text-white text-base md:text-lg leading-snug m-0 flex-1">{q.question_text.replace(/\(Example\)/gi, '').trim()}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2 w-full md:w-auto">
                  {showIncorrectAnswerInReview ? (
                    <div className="exercise-input-text inline-flex items-center bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex-grow md:flex-grow-0 md:min-w-[176px] justify-center">
                      {q.user_answer?.answer
                        ? <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-70 mr-2 font-bold">{q.user_answer.answer}</span>
                        : <span className="text-gray-400 mr-2 font-bold">__</span>
                      }
                      <span className="text-green-600 font-black text-lg">{q.accepted_answers[0]}</span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={showHint || (isReviewMode && isAnswerCorrect) ? q.accepted_answers[0] : answers[q.id] || ''}
                      onChange={(e) => handleAnswerChange(q.id, e)}
                      className={`exercise-input-field exercise-input-text w-full md:w-44 text-center py-3 font-black text-lg ${showHint ? 'hint-input' : ''}`}
                      readOnly={showHint}
                      disabled={isReviewMode || !isHydrated}
                      autoComplete="off"
                    />
                  )}
                  {isReviewMode && (
                    <div className={`ml-2 w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 ${isAnswerCorrect ? 'bg-hupath-leaf' : 'bg-red-500'}`}>
                      <span className="material-symbols-outlined text-sm font-bold">{isAnswerCorrect ? 'check' : 'close'}</span>
                    </div>
                  )}
                  {isReviewMode && <QuestionLearnButton question={q} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default ReadingPart1;
